// src/admin/pages/ApiKeysPage.js
import React, { useState, useEffect, useCallback } from 'react';
import { 
  Save,
  AlertCircle,
  Plus,
  Edit,
  X,
  Key,
  CheckCircle,
  Eye,
  EyeOff,
  RefreshCw
} from 'lucide-react';
import UtilityService from '../../services/utilityService';

const ApiKeysPage = () => {
  const [apiKeys, setApiKeys] = useState([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [editingKey, setEditingKey] = useState(null);
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [showKeyValue, setShowKeyValue] = useState({});
  const [inlineMessage, setInlineMessage] = useState(null);
  const [newKey, setNewKey] = useState({
    service_name: '',
    display_name: '',
    api_key: '',
    api_url: '',
    is_active: true,
    rate_limit_per_minute: null,
    rate_limit_per_day: null,
    notes: ''
  });

  const showMessage = useCallback((type, text) => {
    setInlineMessage({ type, text });
    setTimeout(() => {
      setInlineMessage(null);
    }, 5000);
  }, []);

  // Load API keys from backend
  const loadApiKeys = useCallback(async () => {
    setLoading(true);
    try {
      const headers = await UtilityService.getAuthHeaders();
      const response = await fetch('/api/api-keys', {
        method: 'GET',
        headers: headers
      });

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.error || 'Failed to fetch API keys');
      }

      const data = await response.json();
      if (data.success && data.keys) {
        setApiKeys(data.keys);
      } else {
        throw new Error('Invalid response format');
      }
    } catch (error) {
      console.error('Error loading API keys:', error);
      showMessage('error', 'Error loading API keys: ' + error.message);
    } finally {
      setLoading(false);
    }
  }, [showMessage]);

  useEffect(() => {
    loadApiKeys();
  }, [loadApiKeys]);

  const handleSaveKey = async (keyData) => {
    setSaving(true);
    try {
      const headers = await UtilityService.getAuthHeaders();
      
      if (keyData.id || editingKey?.id) {
        // Update existing key
        const serviceName = keyData.service_name || editingKey.service_name;
        const response = await fetch(`/api/api-keys/${serviceName}`, {
          method: 'PUT',
          headers: headers,
          body: JSON.stringify(keyData)
        });

        if (!response.ok) {
          const errorData = await response.json();
          throw new Error(errorData.error || 'Failed to update API key');
        }

        showMessage('success', 'API key updated successfully!');
      } else {
        // Create new key
        const response = await fetch('/api/api-keys', {
          method: 'POST',
          headers: headers,
          body: JSON.stringify(keyData)
        });

        if (!response.ok) {
          const errorData = await response.json();
          throw new Error(errorData.error || 'Failed to create API key');
        }

        showMessage('success', 'API key created successfully!');
      }

      setEditingKey(null);
      setShowCreateModal(false);
      setNewKey({
        service_name: '',
        display_name: '',
        api_key: '',
        api_url: '',
        is_active: true,
        rate_limit_per_minute: null,
        rate_limit_per_day: null,
        notes: ''
      });
      await loadApiKeys();
    } catch (error) {
      console.error('Error saving API key:', error);
      showMessage('error', 'Error saving API key: ' + error.message);
    } finally {
      setSaving(false);
    }
  };

  const handleEdit = (key) => {
    setEditingKey({ ...key });
    setShowCreateModal(true);
  };

  const handleCreateNew = () => {
    setEditingKey(null);
    setNewKey({
      service_name: '',
      display_name: '',
      api_key: '',
      api_url: '',
      is_active: true,
      rate_limit_per_minute: null,
      rate_limit_per_day: null,
      notes: ''
    });
    setShowCreateModal(true);
  };

  const toggleKeyVisibility = (serviceName) => {
    setShowKeyValue(prev => ({
      ...prev,
      [serviceName]: !prev[serviceName]
    }));
  };

  const maskKey = (key) => {
    if (!key) return '';
    if (key.length <= 8) return '••••••••';
    return key.substring(0, 4) + '••••••••' + key.substring(key.length - 4);
  };

  // API usage information mapping
  const getApiUsageInfo = (serviceName) => {
    const usageMap = {
      'finnhub': {
        dataProvided: [
          'Company profiles (name, exchange, currency, logo)',
          'Real-time stock quotes (price, change, high/low)',
          'Company financial metrics (market cap, enterprise value, shares outstanding)',
          'Company search by symbol or name',
          'Company news articles',
          'Stock candlestick charts (fallback)'
        ],
        pagesUsed: [
          { url: '/research/find-company', name: 'Find Company' },
          { url: '/research/stock/:symbol', name: 'Stock Details' },
          { url: '/research/news/:symbol', name: 'Company News' },
          { url: '/research/chart/:symbol', name: 'Stock Charts' },
          { url: '/research/portfolio', name: 'Portfolio' }
        ]
      },
      'alphavantage': {
        dataProvided: [
          'Historical time series data (daily stock prices)',
          'OHLCV data (Open, High, Low, Close, Volume)',
          'Daily stock price history'
        ],
        pagesUsed: [
          { url: '/research/chart/:symbol', name: 'Stock Charts (Primary)' }
        ]
      },
      'fred': {
        dataProvided: [
          'Treasury bond yields (1MO, 3MO, 6MO, 1Y, 2Y, 3Y, 5Y, 7Y, 10Y, 20Y, 30Y)',
          'Historical Treasury yield data',
          'Yield curve data',
          'Treasury yield changes and trends'
        ],
        pagesUsed: [
          { url: '/research/treasury-bonds', name: 'Treasury Bonds' }
        ]
      }
    };
    return usageMap[serviceName] || { dataProvided: [], pagesUsed: [] };
  };

  if (loading) {
    return (
      <div className="p-8">
        <div className="flex items-center justify-center h-64">
          <div className="text-center">
            <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600 mx-auto"></div>
            <p className="mt-4 text-gray-600">Loading API keys...</p>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="p-8">
      {/* Inline Message */}
      {inlineMessage && (
        <div className={`mb-6 p-4 rounded-lg border-l-4 flex items-center justify-between ${
          inlineMessage.type === 'success' 
            ? 'bg-green-50 border-green-400 text-green-800' 
            : 'bg-red-50 border-red-400 text-red-800'
        }`}>
          <div className="flex items-center">
            {inlineMessage.type === 'success' ? (
              <CheckCircle className="h-5 w-5 mr-3" />
            ) : (
              <AlertCircle className="h-5 w-5 mr-3" />
            )}
            <p className="font-medium">{inlineMessage.text}</p>
          </div>
          <button
            onClick={() => setInlineMessage(null)}
            className="ml-4 text-gray-400 hover:text-gray-600"
          >
            <X className="h-5 w-5" />
          </button>
        </div>
      )}

      {/* Header */}
      <div className="flex justify-between items-center mb-6">
        <div>
          <h2 className="text-2xl font-bold text-gray-900">API Keys Management</h2>
          <p className="text-gray-600 mt-1">Manage external API keys for third-party services</p>
        </div>
        <div className="flex space-x-3">
          <button
            onClick={loadApiKeys}
            className="flex items-center px-4 py-2 bg-gray-600 text-white rounded-md hover:bg-gray-700 transition-colors"
          >
            <RefreshCw className="h-4 w-4 mr-2" />
            Refresh
          </button>
          <button
            onClick={handleCreateNew}
            className="flex items-center px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700 transition-colors"
          >
            <Plus className="h-4 w-4 mr-2" />
            New API Key
          </button>
        </div>
      </div>

      {/* Security Warning */}
      <div className="bg-amber-50 border border-amber-200 rounded-lg p-4 mb-6">
        <div className="flex items-start">
          <AlertCircle className="h-5 w-5 text-amber-600 mt-0.5 mr-3" />
          <div>
            <p className="text-sm font-medium text-amber-800">Security Notice</p>
            <p className="text-sm text-amber-700 mt-1">
              API keys are sensitive credentials. Never commit them to version control. 
              All keys are stored securely in the database and only accessible to super admins.
            </p>
          </div>
        </div>
      </div>

      {/* API Keys List */}
      <div className="bg-white rounded-lg shadow">
        {apiKeys.length === 0 ? (
          <div className="text-center py-12 text-gray-500">
            <Key className="h-12 w-12 mx-auto mb-4 text-gray-400" />
            <p className="text-lg font-medium">No API keys configured</p>
            <p className="text-sm mt-2">Click "New API Key" to add your first key</p>
          </div>
        ) : (
          <div className="overflow-x-auto">
            <table className="min-w-full divide-y divide-gray-200">
              <thead className="bg-gray-50">
                <tr>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Service
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    API Key
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    URL
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Usage & Data
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Rate Limits
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Status
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Actions
                  </th>
                </tr>
              </thead>
              <tbody className="bg-white divide-y divide-gray-200">
                {apiKeys.map((key) => (
                  <tr key={key.id} className="hover:bg-gray-50">
                    <td className="px-6 py-4 whitespace-nowrap">
                      <div>
                        <div className="text-sm font-medium text-gray-900">
                          {key.display_name || key.service_name}
                        </div>
                        <div className="text-sm text-gray-500">{key.service_name}</div>
                      </div>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <div className="flex items-center space-x-2">
                        <code className="text-sm font-mono text-gray-800 bg-gray-100 px-2 py-1 rounded">
                          {showKeyValue[key.service_name] 
                            ? key.api_key 
                            : maskKey(key.api_key)}
                        </code>
                        <button
                          onClick={() => toggleKeyVisibility(key.service_name)}
                          className="text-gray-400 hover:text-gray-600"
                          title={showKeyValue[key.service_name] ? 'Hide key' : 'Show key'}
                        >
                          {showKeyValue[key.service_name] ? (
                            <EyeOff className="h-4 w-4" />
                          ) : (
                            <Eye className="h-4 w-4" />
                          )}
                        </button>
                      </div>
                    </td>
                    <td className="px-6 py-4">
                      <div className="text-sm text-gray-900">
                        {key.api_url ? (
                          <a 
                            href={key.api_url} 
                            target="_blank" 
                            rel="noopener noreferrer"
                            className="text-blue-600 hover:text-blue-800"
                          >
                            {key.api_url}
                          </a>
                        ) : (
                          <span className="text-gray-400">—</span>
                        )}
                      </div>
                    </td>
                    <td className="px-6 py-4">
                      <div className="text-sm text-gray-900 max-w-md">
                        {(() => {
                          const usageInfo = getApiUsageInfo(key.service_name);
                          if (usageInfo.dataProvided.length === 0 && usageInfo.pagesUsed.length === 0) {
                            return <span className="text-gray-400">—</span>;
                          }
                          return (
                            <div className="space-y-2">
                              <div>
                                <span className="font-medium text-gray-700 text-xs">Data Provided:</span>
                                <ul className="list-disc list-inside mt-1 space-y-0.5 text-xs text-gray-600">
                                  {usageInfo.dataProvided.slice(0, 3).map((item, idx) => (
                                    <li key={idx} className="truncate" title={item}>{item}</li>
                                  ))}
                                  {usageInfo.dataProvided.length > 3 && (
                                    <li className="text-gray-500">+{usageInfo.dataProvided.length - 3} more...</li>
                                  )}
                                </ul>
                              </div>
                              <div>
                                <span className="font-medium text-gray-700 text-xs">Used On Pages:</span>
                                <ul className="list-disc list-inside mt-1 space-y-0.5 text-xs">
                                  {usageInfo.pagesUsed.map((page, idx) => (
                                    <li key={idx} className="truncate">
                                      <a 
                                        href={page.url.replace(':symbol', 'AAPL')} 
                                        className="text-blue-600 hover:text-blue-800 hover:underline"
                                        onClick={(e) => {
                                          e.preventDefault();
                                          window.location.href = page.url.replace(':symbol', 'AAPL');
                                        }}
                                        title={page.url}
                                      >
                                        {page.name}
                                      </a>
                                      <span className="text-gray-500 ml-1 font-mono text-xs">({page.url})</span>
                                    </li>
                                  ))}
                                </ul>
                              </div>
                            </div>
                          );
                        })()}
                      </div>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <div className="text-sm text-gray-900">
                        {key.rate_limit_per_minute && (
                          <div>{key.rate_limit_per_minute}/min</div>
                        )}
                        {key.rate_limit_per_day && (
                          <div className="text-gray-500">{key.rate_limit_per_day}/day</div>
                        )}
                        {!key.rate_limit_per_minute && !key.rate_limit_per_day && (
                          <span className="text-gray-400">—</span>
                        )}
                      </div>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <span className={`px-2 inline-flex text-xs leading-5 font-semibold rounded-full ${
                        key.is_active 
                          ? 'bg-green-100 text-green-800' 
                          : 'bg-gray-100 text-gray-800'
                      }`}>
                        {key.is_active ? 'Active' : 'Inactive'}
                      </span>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm font-medium">
                      <button
                        onClick={() => handleEdit(key)}
                        className="text-blue-600 hover:text-blue-900 mr-4"
                      >
                        <Edit className="h-4 w-4" />
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {/* Create/Edit Modal */}
      {showCreateModal && (
        <div className="fixed inset-0 bg-gray-600 bg-opacity-50 overflow-y-auto h-full w-full z-50">
          <div className="relative top-20 mx-auto p-5 border w-11/12 max-w-2xl shadow-lg rounded-md bg-white">
            <div className="mt-3">
              <div className="flex items-center justify-between mb-4">
                <h3 className="text-lg font-medium text-gray-900">
                  {editingKey ? 'Edit API Key' : 'New API Key'}
                </h3>
                <button
                  onClick={() => {
                    setShowCreateModal(false);
                    setEditingKey(null);
                  }}
                  className="text-gray-400 hover:text-gray-600"
                >
                  <X className="h-6 w-6" />
                </button>
              </div>
              
              <div className="space-y-4">
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Service Name *
                    </label>
                    <input
                      type="text"
                      value={editingKey?.service_name || newKey.service_name}
                      onChange={(e) => {
                        if (editingKey) {
                          setEditingKey({ ...editingKey, service_name: e.target.value });
                        } else {
                          setNewKey({ ...newKey, service_name: e.target.value });
                        }
                      }}
                      disabled={!!editingKey}
                      className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 disabled:bg-gray-100"
                      placeholder="e.g., finnhub, alphavantage"
                    />
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Display Name *
                    </label>
                    <input
                      type="text"
                      value={editingKey?.display_name || newKey.display_name}
                      onChange={(e) => {
                        if (editingKey) {
                          setEditingKey({ ...editingKey, display_name: e.target.value });
                        } else {
                          setNewKey({ ...newKey, display_name: e.target.value });
                        }
                      }}
                      className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                      placeholder="e.g., Finnhub API"
                    />
                  </div>
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    API Key *
                  </label>
                  <input
                    type="text"
                    value={editingKey?.api_key || newKey.api_key}
                    onChange={(e) => {
                      if (editingKey) {
                        setEditingKey({ ...editingKey, api_key: e.target.value });
                      } else {
                        setNewKey({ ...newKey, api_key: e.target.value });
                      }
                    }}
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 font-mono"
                    placeholder="Enter API key"
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    API URL
                  </label>
                  <input
                    type="url"
                    value={editingKey?.api_url || newKey.api_url}
                    onChange={(e) => {
                      if (editingKey) {
                        setEditingKey({ ...editingKey, api_url: e.target.value });
                      } else {
                        setNewKey({ ...newKey, api_url: e.target.value });
                      }
                    }}
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                    placeholder="https://api.example.com"
                  />
                </div>

                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Rate Limit (per minute)
                    </label>
                    <input
                      type="number"
                      value={editingKey?.rate_limit_per_minute || newKey.rate_limit_per_minute || ''}
                      onChange={(e) => {
                        const value = e.target.value ? parseInt(e.target.value) : null;
                        if (editingKey) {
                          setEditingKey({ ...editingKey, rate_limit_per_minute: value });
                        } else {
                          setNewKey({ ...newKey, rate_limit_per_minute: value });
                        }
                      }}
                      className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                      placeholder="60"
                    />
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Rate Limit (per day)
                    </label>
                    <input
                      type="number"
                      value={editingKey?.rate_limit_per_day || newKey.rate_limit_per_day || ''}
                      onChange={(e) => {
                        const value = e.target.value ? parseInt(e.target.value) : null;
                        if (editingKey) {
                          setEditingKey({ ...editingKey, rate_limit_per_day: value });
                        } else {
                          setNewKey({ ...newKey, rate_limit_per_day: value });
                        }
                      }}
                      className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                      placeholder="10000"
                    />
                  </div>
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Notes
                  </label>
                  <textarea
                    value={editingKey?.notes || newKey.notes}
                    onChange={(e) => {
                      if (editingKey) {
                        setEditingKey({ ...editingKey, notes: e.target.value });
                      } else {
                        setNewKey({ ...newKey, notes: e.target.value });
                      }
                    }}
                    rows={3}
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                    placeholder="Additional notes about this API key..."
                  />
                </div>

                <label className="flex items-center">
                  <input
                    type="checkbox"
                    checked={editingKey?.is_active !== false && (newKey.is_active !== false)}
                    onChange={(e) => {
                      if (editingKey) {
                        setEditingKey({ ...editingKey, is_active: e.target.checked });
                      } else {
                        setNewKey({ ...newKey, is_active: e.target.checked });
                      }
                    }}
                    className="h-4 w-4 text-blue-600 focus:ring-blue-500 border-gray-300 rounded"
                  />
                  <span className="ml-2 text-sm text-gray-700">Active (Enable this API key)</span>
                </label>
              </div>

              <div className="mt-6 flex justify-end space-x-3">
                <button
                  onClick={() => {
                    setShowCreateModal(false);
                    setEditingKey(null);
                  }}
                  className="px-4 py-2 bg-gray-200 text-gray-700 rounded-md hover:bg-gray-300 transition-colors"
                >
                  Cancel
                </button>
                <button
                  onClick={() => handleSaveKey(editingKey || newKey)}
                  disabled={saving}
                  className="flex items-center px-4 py-2 bg-green-600 text-white rounded-md hover:bg-green-700 transition-colors disabled:opacity-50"
                >
                  <Save className="h-4 w-4 mr-2" />
                  {saving ? 'Saving...' : 'Save'}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default ApiKeysPage;

